import {
    useState,
    Dispatch,
    SetStateAction,
    useCallback,
    useMemo,
    PropsWithChildren
} from "react";

import { useIntl } from "react-intl";

import { USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL } from "@atlassian/onboarding-survey";

import { useSearchParameters } from "@/components/signup/hooks/use-search-parameters";

import { Setup } from "..";
import { useCloudStatus } from "../../../hooks/use-cloud-status";
import { useConfirmationPage } from "../../../hooks/use-confirmation-page.tsx";
import { useMatureExampleProjectWithExperiments } from "../../../hooks/use-mature-example-project-with-experiments";
import { TemplateQueryParamScrum } from "../../../utils/utils.ts";
import { getDefaultProjectName } from "../../onboarding-wizard/wizard/page-project-details/left-hand-side/utils.ts";
import { Jfind1663CreateProjectTaskPollingProvider as InviteWrapperProvider } from "../../onboarding-wizard/wizard/variant/jfind-1663-page-invite/hooks/use-jfind-1663-create-project-task-polling-context.tsx";
import { useJfind1663FetchInterceptor } from "../../onboarding-wizard/wizard/variant/jfind-1663-page-invite/hooks/use-jfind-1663-fetch-interceptor.tsx";
import { Invite } from "../common/steps/invite/invite";
import { Loading } from "../common/steps/loading/loading";
import { Madlib, MadlibStepContext } from "../common/steps/madlib/madlib";
import { Preview } from "../common/steps/preview/preview";
import { SpaceDetails } from "../common/steps/space-details/space-details";
import { TemplateOptionScrum, TemplateOptionKanban } from "../types.tsx";

import {
    SETUP_SCREEN_INDEX_INVITE,
    SETUP_SCREEN_INDEX_SPACE_DETAILS
} from "./constants";
import {
    getDateDaysFromNow,
    onLoadingStep,
    useBulkEmailInvite,
    useCreateProject
} from "./utils";

export { JiraFflSetup, type JiraFflStepContext } from "./jira-ffl-setup";

export const JiraSetup = () => {
    const { cloudId: tenantId } = useCloudStatus();
    const { formatMessage } = useIntl();
    const { createMadlibProject, createDefaultProject } = useCreateProject();
    const { postBulkEmailInvite } = useBulkEmailInvite();
    const { setMatureSampleProjectEligibility } = useMatureSampleProject();

    const {
        templateChoice,
        jtbdTemplateOverride,
        teamType: teamTypeFromQueryParam,
        taskIdParam
    } = useSearchParameters();

    const {
        userSegmentationContext: { selectedTeamType: teamTypeFromContext }
    } = useConfirmationPage();

    const teamType = useMemo(() => {
        const normalizeTeamType = (value?: string | null) =>
            value === "" || value === null ? undefined : value;

        return (
            normalizeTeamType(teamTypeFromContext) ??
            normalizeTeamType(teamTypeFromQueryParam) ??
            USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL
        );
    }, [teamTypeFromContext, teamTypeFromQueryParam]);

    const spaceName = useMemo(
        () => getDefaultProjectName(teamType ?? "", "", formatMessage),
        [teamType, formatMessage]
    );

    const template = useMemo(() => {
        switch (jtbdTemplateOverride ?? templateChoice) {
            case TemplateQueryParamScrum:
                return TemplateOptionScrum;
            default:
                return TemplateOptionKanban;
        }
    }, [jtbdTemplateOverride, templateChoice]);

    const initialContext: MadlibStepContext = useMemo(() => {
        return {
            tenantId,
            teamType,
            spaceName,
            dueDate: getDateDaysFromNow(14),
            template,
            promisesToResolve: [],
            taskId: taskIdParam ?? undefined
        };
    }, [template, teamType, spaceName, tenantId, taskIdParam]);

    return (
        <InviteWrapperProvider>
            <InviteInterceptor>
                <Setup<MadlibStepContext>
                    initialContext={initialContext}
                    initialScreenIndex={
                        taskIdParam
                            ? SETUP_SCREEN_INDEX_INVITE
                            : SETUP_SCREEN_INDEX_SPACE_DETAILS
                    }
                    screens={[
                        {
                            type: "split",
                            screenId: "space-details",
                            LeftComponent: SpaceDetails,
                            RightComponent: Preview,
                            onSubmit: setMatureSampleProjectEligibility
                        },
                        {
                            type: "split",
                            screenId: "madlib-setup-prompt",
                            LeftComponent: Madlib,
                            RightComponent: Preview,
                            onSubmit: createMadlibProject,
                            onSkip: createDefaultProject
                        },
                        {
                            type: "split",
                            screenId: "invite",
                            LeftComponent: Invite,
                            RightComponent: Preview,
                            onSubmit: postBulkEmailInvite
                        },
                        {
                            type: "split",
                            screenId: "loading",
                            LeftComponent: Loading,
                            RightComponent: Preview,
                            onLoad: onLoadingStep
                        }
                    ]}
                />
            </InviteInterceptor>
        </InviteWrapperProvider>
    );
};

export const InviteInterceptor = ({ children }: PropsWithChildren) => {
    const { cloudId: tenantId } = useCloudStatus();
    // setup fetch interceptor for invite component
    useJfind1663FetchInterceptor({ cloudId: tenantId });
    return <>{children}</>;
};

const useMatureSampleProject = () => {
    const [isEligibleForSampleProject, setIsEligibleForSampleProject] =
        useState(false);

    // create mature sample project only if eligible
    useMatureExampleProjectWithExperiments(isEligibleForSampleProject);

    const setMatureSampleProjectEligibility = useCallback(
        async ({
            context
        }: {
            context: MadlibStepContext;
            setContext: Dispatch<SetStateAction<MadlibStepContext>>;
        }) => {
            setIsEligibleForSampleProject(context.template === "KANBAN");
        },
        []
    );

    return { setMatureSampleProjectEligibility };
};
